import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { resolveBackendBase } from '../utils/backendBase.js';
import { evaluatePassword, passwordsMatch } from '../utils/passwordPolicy.js';

export default function AccountSetup() {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const policy = evaluatePassword(password);
  const matching = passwordsMatch(password, confirmation);
  const submit = async event => {
    event.preventDefault();
    setError('');
    if (!token.trim()) {
      setError('أدخل رمز إعداد الحساب.');
      return;
    }
    if (!policy.eligible || !matching) {
      setError('اختر كلمة مرور مطابقة تستوفي متطلبات الأمان.');
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${resolveBackendBase()}/api/auth/setup-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ setup_token: token.trim(), new_password: password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(data?.detail || 'تعذر إعداد الحساب');
      setStatus('تم تعيين كلمة المرور. سجّل الدخول الآن.');
      setToken('');
      setPassword('');
      setConfirmation('');
      // The server invalidates trusted devices when enrollment sets a new
      // credential. Remove any stale local PIN marker before returning home.
      localStorage.removeItem('trusted_device');
    }
    catch (reason) {
      setError(reason.message || 'تعذر إعداد الحساب');
    }
    finally {
      setSaving(false);
    }
  };
  return _jsx("main", { className: "min-h-screen flex items-center justify-center p-4 bg-slate-50", children: _jsxs("section", { className: "apple-card w-full max-w-md p-7 space-y-5", children: [
    _jsxs("div", { className: "text-center space-y-2", children: [_jsx("div", { className: "mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-white", children: _jsx(ShieldCheck, { size: 22 }) }), _jsx("h1", { className: "text-xl font-bold text-slate-900", children: "إعداد أو استعادة كلمة المرور" }), _jsx("p", { className: "text-sm text-slate-600", children: "أدخل رمز الاستعادة الذي أصدره لك مدير النظام. الرمز صالح لمرة واحدة فقط." })] }),
    status ? _jsxs("div", { className: "space-y-4", children: [_jsx("p", { className: "rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800", role: "status", children: status }), _jsx("button", { className: "apple-button w-full", onClick: () => navigate('/login'), children: "الانتقال إلى تسجيل الدخول" })] }) : _jsxs("form", { className: "space-y-4", onSubmit: submit, children: [
      _jsxs("label", { className: "block space-y-2", children: [_jsx("span", { className: "text-sm font-medium text-slate-800", children: "رمز الإعداد" }), _jsx("input", { required: true, autoComplete: "one-time-code", value: token, onChange: event => setToken(event.target.value), className: "apple-input", placeholder: "setup_…", "data-testid": "account-setup-token" })] }),
      _jsxs("label", { className: "block space-y-2", children: [_jsx("span", { className: "text-sm font-medium text-slate-800", children: "كلمة المرور الجديدة" }), _jsx("input", { required: true, type: "password", autoComplete: "new-password", value: password, onChange: event => setPassword(event.target.value), className: "apple-input", "data-testid": "account-setup-password" })] }),
      _jsxs("label", { className: "block space-y-2", children: [_jsx("span", { className: "text-sm font-medium text-slate-800", children: "تأكيد كلمة المرور" }), _jsx("input", { required: true, type: "password", autoComplete: "new-password", value: confirmation, onChange: event => setConfirmation(event.target.value), className: "apple-input", "data-testid": "account-setup-confirmation" })] }),
      _jsxs("p", { className: "text-xs", style: { color: policy.eligible ? '#15803d' : '#64748b' }, children: [_jsx(KeyRound, { size: 13, className: "inline ml-1" }), "12 حرفاً على الأقل وثلاثة أنواع من الأحرف."] }),
      error && _jsx("p", { role: "alert", className: "rounded-xl bg-red-50 p-3 text-sm text-red-700", children: error }),
      _jsx("button", { type: "submit", disabled: saving || !policy.eligible || !matching, className: "apple-button w-full disabled:opacity-50", "data-testid": "account-setup-submit", children: saving ? 'جارٍ الإعداد…' : 'تعيين كلمة المرور' }),
      _jsx("button", { type: "button", className: "w-full text-sm text-blue-700 hover:underline", onClick: () => navigate('/login'), children: "العودة إلى تسجيل الدخول" }),
    ] }),
  ] }) });
}

